/**
 * Viewport mode-transition overlay.
 *
 * Switching between draft (`viewport` scope) and output (`render`/`inking`
 * scope) triggers heavy one-shot work in the renderer — geometry subdivision,
 * GPU normal-pipeline rebuild, post-processing pipeline creation, material
 * recompile. That work stalls the render thread for one or more frames, which
 * the user perceives as a dramatic flash.
 *
 * Unifying viewport DPR + shadow-map size (see `ViewportQualitySettings`)
 * removes the canvas buffer reallocation cost, but the rest of the stall
 * remains on the first-entry path. This overlay masks the stall with a brief
 * fade: opaque backdrop on scope change, fade out once the renderer is
 * stable again.
 *
 * Stability is detected by a short rAF loop — a run of short-delta frames
 * signals that the heavy setup has settled. If the run takes longer than the
 * spinner threshold the CubeSpinner is revealed so the user knows the app
 * is working.
 */

import { useEffect, useRef, useState } from "react";
import { useActiveRenderScope } from "../app/renderConfig.ts";
import { CubeSpinner } from "../ui/CubeSpinner.tsx";
import { COLOR_BG } from "../ui/tokens.ts";

/** Fade in/out duration for the opaque overlay. */
const FADE_MS = 140;
/** Minimum time the overlay stays opaque — covers the first-frame stall. */
const MIN_HOLD_MS = 200;
/** Spinner appears if stabilisation runs longer than this. */
const SPINNER_THRESHOLD_MS = 500;
/** Hard cap — stop covering even if the stability signal never fires. */
const MAX_HOLD_MS = 3000;
/** Consecutive good-frame count required to declare the renderer stable. */
const STABLE_FRAMES_REQUIRED = 3;
/** Frame delta above this ms is treated as a stalled frame. */
const MAX_STABLE_FRAME_MS = 28;

type Phase = "idle" | "cover" | "fade-out";

export function ViewportModeTransition() {
  const scope = useActiveRenderScope();
  const [phase, setPhase] = useState<Phase>("idle");
  const [showSpinner, setShowSpinner] = useState(false);
  const prevScope = useRef(scope);

  useEffect(() => {
    if (prevScope.current === scope) return;
    prevScope.current = scope;

    setPhase("cover");
    setShowSpinner(false);
    const startedAt = performance.now();

    let rafId: number | null = null;
    let lastFrameT = startedAt;
    let goodFrames = 0;
    let spinnerTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      setShowSpinner(true);
    }, SPINNER_THRESHOLD_MS);
    let hardCapTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      finish();
    }, MAX_HOLD_MS);

    const finish = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (spinnerTimer != null) {
        clearTimeout(spinnerTimer);
        spinnerTimer = null;
      }
      if (hardCapTimer != null) {
        clearTimeout(hardCapTimer);
        hardCapTimer = null;
      }
      setPhase("fade-out");
      setShowSpinner(false);
    };

    const tick = (now: number) => {
      const dt = now - lastFrameT;
      lastFrameT = now;
      // Skip the very first rAF — its delta reflects time since the effect
      // ran, not inter-frame cost.
      if (now - startedAt > 16) {
        if (dt <= MAX_STABLE_FRAME_MS) goodFrames++;
        else goodFrames = 0;
      }

      const elapsed = now - startedAt;
      if (goodFrames >= STABLE_FRAMES_REQUIRED && elapsed >= MIN_HOLD_MS) {
        finish();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      if (spinnerTimer != null) clearTimeout(spinnerTimer);
      if (hardCapTimer != null) clearTimeout(hardCapTimer);
    };
  }, [scope]);

  // After fade-out completes, return to idle so the overlay unmounts cleanly.
  useEffect(() => {
    if (phase !== "fade-out") return;
    const t = setTimeout(() => setPhase("idle"), FADE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === "idle") return null;

  const isOpaque = phase === "cover";

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background: COLOR_BG,
        opacity: isOpaque ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Canvas sits at z-index 2 (see Viewport3D). Overlay must sit above
        // it but below modals.
        zIndex: 3,
      }}
    >
      {showSpinner && <CubeSpinner size={48} />}
    </div>
  );
}
